/*
 * THE HOUSE OF JK — core storefront behaviour.
 * Ported from the React SPA (the-house-of-jk-bilingual.jsx):
 *   - main component state + handlers .... JSX 2352-2615
 *   - header / index panel / cart drawer . JSX 2616-2838
 *   - playCapClick() ..................... JSX 935-997 (verbatim)
 *   - FAB stack + patron pop ............. JSX 3304-3332
 *
 * Vanilla JS. No framework, no CDN, no build step. Everything here is
 * progressive enhancement: with JS disabled the add-to-cart forms, the
 * quantity forms, the search form and every link still work as plain
 * server-rendered HTML.
 *
 * Every block below is independently guarded — a page that is missing any
 * of these elements must never throw.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  var routes = window.routes || {};
  var strings = window.jkStrings || {};
  var settings = window.jkSettings || {};
  var shopifyRoot =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var prefersReducedMotion = false;
  try {
    prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    prefersReducedMotion = false;
  }

  function $(selector, scope) {
    try {
      return (scope || document).querySelector(selector);
    } catch (e) {
      return null;
    }
  }

  function $$(selector, scope) {
    try {
      return Array.prototype.slice.call(
        (scope || document).querySelectorAll(selector)
      );
    } catch (e) {
      return [];
    }
  }

  function on(el, type, handler, options) {
    if (el && el.addEventListener) el.addEventListener(type, handler, options);
  }

  /* scrollIntoView is absent in some embedded webviews — never let a smooth
     scroll be the thing that breaks the page. */
  function scrollToEl(el) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    try {
      el.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    } catch (e) {
      el.scrollIntoView();
    }
  }

  /* Marks an element as wired so the theme editor's shopify:section:load
     can safely re-run an initializer without stacking duplicate listeners. */
  function claim(el, key) {
    if (!el) return false;
    var attr = 'data-jk-' + key;
    if (el.hasAttribute(attr)) return false;
    el.setAttribute(attr, '');
    return true;
  }

  function parseJSONAttr(el, attr) {
    if (!el) return null;
    var raw = el.getAttribute(attr);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /* Shopify's standard money formatter, so JS-built markup matches | money. */
  function formatMoney(cents, format) {
    var fmt = format || window.moneyFormat || '${{amount}}';
    var value = typeof cents === 'string' ? cents.replace(/[^0-9.-]/g, '') : cents;
    value = Number(value);
    if (isNaN(value)) return '';

    function thousands(number, sep, decSep, precision) {
      var p = typeof precision === 'undefined' ? 2 : precision;
      var s = typeof sep === 'undefined' ? ',' : sep;
      var d = typeof decSep === 'undefined' ? '.' : decSep;
      var num = (number / 100.0).toFixed(p);
      var parts = num.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + s);
      var frac = parts[1] ? d + parts[1] : '';
      return whole + frac;
    }

    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholder = match ? match[1] : 'amount';
    var formatted;

    switch (placeholder) {
      case 'amount_no_decimals':
        formatted = thousands(value, ',', '.', 0);
        break;
      case 'amount_with_comma_separator':
        formatted = thousands(value, '.', ',', 2);
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = thousands(value, '.', ',', 0);
        break;
      case 'amount_with_apostrophe_separator':
        formatted = thousands(value, "'", '.', 2);
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = thousands(value, ' ', '.', 0);
        break;
      case 'amount_with_space_separator':
        formatted = thousands(value, ' ', ',', 2);
        break;
      default:
        formatted = thousands(value, ',', '.', 2);
    }

    return fmt.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  /* --------------------------------------------------- body scroll locking */

  var scrollLocks = 0;
  var previousBodyOverflow = null;

  function lockScroll() {
    scrollLocks += 1;
    if (scrollLocks === 1 && document.body) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
  }

  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0 && document.body) {
      document.body.style.overflow = previousBodyOverflow || '';
      previousBodyOverflow = null;
    }
  }

  /* ------------------------------------------------------------ focus trap */

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), iframe, object, embed, ' +
    '[tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  var trapContainer = null;
  var trapReturnFocus = null;

  function onTrapKeydown(event) {
    if (event.key !== 'Tab' || !trapContainer) return;
    var items = $$(FOCUSABLE, trapContainer).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function trapFocus(container, returnTo) {
    if (!container) return;
    releaseFocus(false);
    trapContainer = container;
    trapReturnFocus = returnTo || document.activeElement;
    document.addEventListener('keydown', onTrapKeydown, true);
    var first = $$(FOCUSABLE, container)[0];
    if (first) {
      try {
        first.focus({ preventScroll: true });
      } catch (e) {
        first.focus();
      }
    }
  }

  function releaseFocus(restore) {
    if (!trapContainer) return;
    document.removeEventListener('keydown', onTrapKeydown, true);
    trapContainer = null;
    var target = trapReturnFocus;
    trapReturnFocus = null;
    if (restore !== false && target && typeof target.focus === 'function') {
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
    }
  }

  /* ----------------------------------------------------------------- toast */
  /* .toast markup lives in snippets/toast.liquid. The message span is
     cached on first use so an error message can replace it and the
     localized "Added <name> to cart" sentence can be restored afterwards. */

  var toastEl = null;
  var toastMessageEl = null;
  var toastMessageTemplate = '';
  var toastTimer = null;

  function initToast() {
    toastEl = $('[data-toast]');
    if (!toastEl) return;
    var nameEl = $('[data-toast-name]', toastEl);
    toastMessageEl = nameEl ? nameEl.parentNode : null;
    if (toastMessageEl) toastMessageTemplate = toastMessageEl.innerHTML;
  }

  function showToast(productTitle) {
    if (!toastEl) return;
    if (toastMessageEl && toastMessageTemplate) {
      toastMessageEl.innerHTML = toastMessageTemplate;
      var nameEl = $('[data-toast-name]', toastEl);
      if (nameEl) nameEl.textContent = productTitle || '';
    }
    flashToast();
  }

  function showToastMessage(message) {
    if (!toastEl || !message) return;
    if (toastMessageEl) toastMessageEl.textContent = message;
    flashToast();
  }

  function flashToast() {
    toastEl.classList.add('show');
    clearTimeout(toastTimer);
    // 2200ms — the exact beat from the SPA (JSX line ~2404).
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('show');
    }, 2200);
  }

  /* ----------------------------------------------------- playCapClick (JSX 935-997) */
  // Synthesizes the sound of using the perfume — the snap-click of the cap
  // seating on glass, then a short spray/mist hiss right after — with the Web
  // Audio API (no audio file needed).
  function playCapClick() {
    try {
      var AudioCtx = window.AudioContext || window.webkitAudioContext;
      var ctx = new AudioCtx();
      var now = ctx.currentTime;

      // 1. The "tek" — sharp click of the cap snapping
      var snap = ctx.createOscillator();
      var snapGain = ctx.createGain();
      snap.type = 'square';
      snap.frequency.setValueAtTime(3400, now);
      snap.frequency.exponentialRampToValueAtTime(1800, now + 0.02);
      snapGain.gain.setValueAtTime(0.0001, now);
      snapGain.gain.exponentialRampToValueAtTime(0.5, now + 0.003);
      snapGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
      snap.connect(snapGain).connect(ctx.destination);
      snap.start(now);
      snap.stop(now + 0.05);

      // 2. The "tak" — cap settling, a touch lower
      var thud = ctx.createOscillator();
      var thudGain = ctx.createGain();
      thud.type = 'sine';
      thud.frequency.setValueAtTime(520, now + 0.05);
      thudGain.gain.setValueAtTime(0.0001, now + 0.05);
      thudGain.gain.exponentialRampToValueAtTime(0.25, now + 0.055);
      thudGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.13);
      thud.connect(thudGain).connect(ctx.destination);
      thud.start(now + 0.05);
      thud.stop(now + 0.14);

      // 3. The "psst" — a short spray/mist hiss right after the click
      var sprayStart = now + 0.16;
      var sprayDuration = 0.34;
      var bufferSize = Math.floor(ctx.sampleRate * sprayDuration);
      var noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      var data = noiseBuffer.getChannelData(0);
      for (var i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

      var noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;

      var bandpass = ctx.createBiquadFilter();
      bandpass.type = 'bandpass';
      bandpass.Q.value = 0.7;
      bandpass.frequency.setValueAtTime(7200, sprayStart);
      bandpass.frequency.exponentialRampToValueAtTime(2200, sprayStart + sprayDuration);

      var noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.0001, sprayStart);
      noiseGain.gain.exponentialRampToValueAtTime(0.3, sprayStart + 0.02);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, sprayStart + sprayDuration);

      noise.connect(bandpass).connect(noiseGain).connect(ctx.destination);
      noise.start(sprayStart);
      noise.stop(sprayStart + sprayDuration);

      setTimeout(function () {
        ctx.close();
      }, 900);
    } catch (e) {
      // Web Audio unsupported/blocked — fail silently, add-to-cart still works
    }
  }

  /* ------------------------------------------------------------ spray puff */
  /* JSX 835-841 rendered <span class="spray-puff"> inside the card media for
     the duration of the add pulse. Same markup, injected at runtime. */

  function sprayPuff(button) {
    if (!button) return;
    button.classList.add('add-pulse');
    var card = button.closest ? button.closest('[data-product-card]') : null;
    var media = card ? $('[data-product-card-media]', card) : null;
    var puff = null;

    if (media) {
      puff = document.createElement('span');
      puff.className = 'spray-puff';
      ['spray-puff__mist', 'spray-puff__mist spray-puff__mist--2', 'spray-puff__mist spray-puff__mist--3'].forEach(
        function (cls) {
          var mist = document.createElement('span');
          mist.className = cls;
          puff.appendChild(mist);
        }
      );
      media.appendChild(puff);
    }

    // 700ms — the exact beat from the SPA (JSX line ~2409).
    setTimeout(function () {
      button.classList.remove('add-pulse');
      if (puff && puff.parentNode) puff.parentNode.removeChild(puff);
    }, 700);
  }

  /* ------------------------------------------------------------ cart drawer */
  /*
   * DRAWER RE-RENDER STRATEGY (one approach, as required):
   * The drawer is a SNIPPET rendered from layout/theme.liquid, not a section,
   * so the Section Rendering API cannot address it. Instead we re-fetch
   * routes.cart_url, parse it with DOMParser, and swap in the server-rendered
   * [data-cart-body] / [data-cart-count] / [data-cart-count-label] nodes.
   * That keeps money formatting, translations, line item properties and the
   * bottle-svg fallback entirely server-side — no cart markup is duplicated
   * in JS. /cart.js is used only as a fallback to keep the count honest if
   * the HTML parse fails.
   *
   * The /cart PAGE is a different beast: sections/main-cart.liquid has no
   * [data-cart-body] wrapper, and its line items, its summary lines and its
   * Subtotal / Total live outside anything the drawer swap touches. Since
   * initCart() preventDefault()s every [data-cart-change] submit — including
   * the ones cart-line.liquid renders on the cart page — the page would
   * otherwise show stale quantities and a stale total after a real,
   * server-side cart change. main-cart IS a real section, so we address it by
   * its shopify-section id, taken from the very same parsed document, and
   * replace its contents. Still 100% server-rendered — every price stays
   * `| money`.
   */

  var cartDrawer = null;
  var cartOverlay = null;
  var cartOpenTimer = null;
  var cartIsOpen = false;

  function openCart() {
    if (!cartDrawer || cartIsOpen) return;
    cartIsOpen = true;
    cartDrawer.classList.add('open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    if (cartOverlay) cartOverlay.classList.add('show');
    lockScroll();
    trapFocus(cartDrawer, document.activeElement);
  }

  function closeCart() {
    if (!cartDrawer || !cartIsOpen) return;
    cartIsOpen = false;
    cartDrawer.classList.remove('open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    if (cartOverlay) cartOverlay.classList.remove('show');
    unlockScroll();
    releaseFocus(true);
  }

  function setCartCount(count) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
  }

  /* Locates the cart page section inside a document (live or parsed) and
     returns its shopify-section id. The marker is read from main-cart.liquid:
     [data-cart-form] when the cart has items, .checkout-empty when it does
     not. Scoping the lookup to the parsed /cart document keeps .checkout-empty
     unambiguous — main-collection / main-search / main-blog reuse that class,
     but none of them render on /cart. */
  function cartSectionId(scope) {
    var marker = $('[data-cart-form]', scope) || $('.checkout-empty', scope);
    if (!marker || !marker.closest) return null;
    var section = marker.closest('[id^="shopify-section-"]');
    return section ? section.id : null;
  }

  /* Repaints the /cart page from the already-parsed cart document. A no-op on
     every other page, where the drawer swap above is the whole job. */
  function refreshCartPage(doc) {
    var liveMain = $('[data-cart-main]');
    var freshMain = liveMain ? $('[data-cart-main]', doc) : null;

    if (!liveMain) {
      var sectionId = cartSectionId(doc);
      if (!sectionId) return;
      liveMain = document.getElementById(sectionId);
      freshMain = doc.getElementById(sectionId);
    }
    if (!liveMain || !freshMain) return;

    // A note typed but not yet submitted must survive the repaint.
    var liveNote = $('[data-cart-note]', liveMain);
    var pendingNote = liveNote ? liveNote.value : null;

    liveMain.innerHTML = freshMain.innerHTML;

    if (pendingNote !== null) {
      var freshNote = $('[data-cart-note]', liveMain);
      if (freshNote && freshNote.value !== pendingNote) freshNote.value = pendingNote;
    }
  }

  function refreshCart() {
    if (!routes.cart_url) return Promise.resolve();
    return fetch(routes.cart_url, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart fetch failed');
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var freshBody = $('[data-cart-body]', doc);
        var liveBody = $('[data-cart-body]');
        if (freshBody && liveBody) liveBody.innerHTML = freshBody.innerHTML;

        refreshCartPage(doc);

        var freshCount = $('[data-cart-count]', doc);
        if (freshCount) setCartCount(freshCount.textContent.trim());

        var freshLabel = $('[data-cart-count-label]', doc);
        var liveLabel = $('[data-cart-count-label]');
        if (freshLabel && liveLabel) liveLabel.textContent = freshLabel.textContent;
      })
      .catch(function () {
        // HTML path failed — at minimum keep the bubble truthful.
        return fetch(shopifyRoot.replace(/\/$/, '') + '/cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .then(function (cart) {
            if (cart && typeof cart.item_count !== 'undefined') {
              setCartCount(cart.item_count);
            }
          })
          .catch(function () {
            /* offline — leave the DOM as it is */
          });
      });
  }

  function errorMessageFrom(payload) {
    if (!payload) return strings.cartError || '';
    return (
      payload.description ||
      payload.message ||
      (payload.errors && String(payload.errors)) ||
      strings.cartError ||
      ''
    );
  }

  function initCart() {
    cartDrawer = $('[data-cart-drawer]');
    cartOverlay = $('[data-cart-overlay]');

    // Open / close controls.
    on(document, 'click', function (event) {
      var opener = event.target.closest && event.target.closest('[data-cart-open]');
      if (opener && cartDrawer && settings.cartType !== 'page') {
        event.preventDefault();
        openCart();
        return;
      }

      var closer = event.target.closest && event.target.closest('[data-cart-close]');
      if (closer) {
        event.preventDefault();
        closeCart();
        return;
      }

      if (cartOverlay && event.target === cartOverlay) {
        closeCart();
        return;
      }

      // "Continue Browsing" — close, then scroll, as the SPA did (JSX 2768-2772).
      var cont = event.target.closest && event.target.closest('[data-cart-continue]');
      if (cont) {
        var anchor = $('#all-products');
        if (anchor) {
          event.preventDefault();
          closeCart();
          setTimeout(function () {
            scrollToEl(anchor);
          }, 350);
        }
      }
    });

    /* Add to cart — every [data-add-to-cart] form on the page, present or
       injected later (bundle builder), thanks to the delegated listener. */
    on(document, 'submit', function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('[data-add-to-cart]')) return;
      if (!routes.cart_add_url) return; // no route — let the browser POST
      event.preventDefault();

      var button = event.submitter || form.querySelector('[type="submit"]') || null;
      if (button && button.disabled) return;
      if (button) button.setAttribute('aria-busy', 'true');

      fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
        credentials: 'same-origin'
      })
        .then(function (res) {
          return res.json().then(function (payload) {
            return { ok: res.ok, status: res.status, payload: payload };
          });
        })
        .then(function (result) {
          if (button) button.removeAttribute('aria-busy');

          // 422 = sold out / not enough stock. Surface it, never fail silently.
          if (!result.ok) {
            showToastMessage(errorMessageFrom(result.payload));
            return;
          }

          playCapClick();
          sprayPuff(button);
          showToast(
            (result.payload && (result.payload.product_title || result.payload.title)) || ''
          );

          return refreshCart().then(function () {
            if (settings.cartType === 'page') {
              if (routes.cart_url) window.location.href = routes.cart_url;
              return;
            }
            // 550ms — the SPA opened the drawer a beat after the add (JSX ~2415).
            clearTimeout(cartOpenTimer);
            cartOpenTimer = setTimeout(openCart, 550);
          });
        })
        .catch(function () {
          if (button) button.removeAttribute('aria-busy');
          showToastMessage(strings.cartError || '');
        });
    });

    /* Quantity +/- and remove. Both are real <form method="post"> elements
       keyed on the line item key, so they still work without JS. */
    on(document, 'submit', function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('[data-cart-change]')) return;
      if (!routes.cart_change_url) return;

      var idField = form.querySelector('[name="id"]');
      if (!idField) return;

      // The +/- buttons carry the new quantity; remove uses a hidden input.
      var submitter = event.submitter;
      var quantity = null;
      if (submitter && submitter.name === 'quantity') {
        quantity = submitter.value;
      } else {
        var qtyField = form.querySelector('[name="quantity"]');
        if (qtyField) quantity = qtyField.value;
      }
      if (quantity === null || quantity === '') return;

      event.preventDefault();

      fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: idField.value, quantity: Number(quantity) }),
        credentials: 'same-origin'
      })
        .then(function (res) {
          return res.json().then(function (payload) {
            return { ok: res.ok, payload: payload };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            showToastMessage(errorMessageFrom(result.payload));
            return;
          }
          return refreshCart();
        })
        .catch(function () {
          showToastMessage(strings.cartError || '');
        });
    });
  }

  /* ---------------------------------------------------------------- header */

  var navToggle = null;
  var indexPanel = null;
  var indexOverlay = null;
  var navIsOpen = false;

  function openNav() {
    if (!indexPanel || navIsOpen) return;
    navIsOpen = true;
    indexPanel.classList.add('open');
    indexPanel.setAttribute('aria-hidden', 'false');
    if (indexOverlay) indexOverlay.classList.add('show');
    if (navToggle) {
      navToggle.classList.add('open');
      navToggle.setAttribute('aria-expanded', 'true');
    }
    lockScroll();
    trapFocus(indexPanel, navToggle);
  }

  function closeNav() {
    if (!indexPanel || !navIsOpen) return;
    navIsOpen = false;
    indexPanel.classList.remove('open');
    indexPanel.setAttribute('aria-hidden', 'true');
    if (indexOverlay) indexOverlay.classList.remove('show');
    if (navToggle) {
      navToggle.classList.remove('open');
      navToggle.setAttribute('aria-expanded', 'false');
    }
    unlockScroll();
    releaseFocus(true);
  }

  var headerGlobalsBound = false;

  function initHeader() {
    navToggle = $('[data-nav-toggle]');
    indexPanel = $('#index-panel');
    indexOverlay = $('[data-index-overlay]') || $('#index-overlay');
    navIsOpen = indexPanel ? indexPanel.classList.contains('open') : false;

    if (claim(navToggle, 'navtoggle')) {
      on(navToggle, 'click', function (event) {
        event.preventDefault();
        if (navIsOpen) closeNav();
        else openNav();
      });
    }

    if (claim(indexOverlay, 'indexoverlay')) on(indexOverlay, 'click', closeNav);

    // In-page anchors inside the panel close it, then scroll (JSX scrollToId).
    if (claim(indexPanel, 'indexpanel')) on(indexPanel, 'click', function (event) {
      var link = event.target.closest && event.target.closest('a[href]');
      if (!link) return;
      var href = link.getAttribute('href') || '';
      var hashIndex = href.indexOf('#');
      if (hashIndex === -1) {
        closeNav();
        return;
      }
      var id = href.slice(hashIndex + 1);
      var target = id ? document.getElementById(id) : null;
      if (!target) {
        closeNav();
        return;
      }
      event.preventDefault();
      closeNav();
      scrollToEl(target);
    });

    // Category dropdown inside the index panel.
    $$('[data-idx-cat-toggle]').forEach(function (toggle) {
      if (!claim(toggle, 'idxcat')) return;
      var group = toggle.closest ? toggle.closest('[data-idx-cat]') : null;
      if (!group) return;

      function toggleCat() {
        var willOpen = !group.classList.contains('open');
        group.classList.toggle('open', willOpen);
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      }

      on(toggle, 'click', function (event) {
        event.preventDefault();
        toggleCat();
      });
      on(toggle, 'keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          toggleCat();
        }
      });
    });

    // Mobile search row. Inline display:none is what hides it initially, so
    // clearing the inline value hands control back to theme.css.
    var searchToggle = $('[data-mobile-search-toggle]');
    var searchRow = $('[data-mobile-search-row]');
    if (claim(searchToggle, 'mobilesearch')) {
      on(searchToggle, 'click', function (event) {
        event.preventDefault();
        if (!searchRow) return;
        var isOpen = searchRow.style.display !== 'none';
        searchRow.style.display = isOpen ? 'none' : '';
        searchToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        if (!isOpen) {
          var input = $('[data-predictive-input]', searchRow);
          if (input) input.focus();
        }
      });
    }

    // Sticky-on-scroll state. #topnav is already position:sticky in theme.css;
    // this only exposes a data hook so the header can be styled once scrolled.
    var header = $('[data-header]');
    if (header && claim(header, 'sticky')) {
      var lastScrolled = null;
      var ticking = false;
      var applyScrollState = function () {
        var scrolled = (window.pageYOffset || document.documentElement.scrollTop) > 10;
        if (scrolled !== lastScrolled) {
          lastScrolled = scrolled;
          header.setAttribute('data-scrolled', scrolled ? 'true' : 'false');
        }
        ticking = false;
      };
      on(
        window,
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(applyScrollState);
        },
        { passive: true }
      );
      applyScrollState();
    }

    if (headerGlobalsBound) return;
    headerGlobalsBound = true;

    // Escape closes the topmost overlay.
    on(document, 'keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (cartIsOpen) {
        closeCart();
        return;
      }
      if (navIsOpen) {
        closeNav();
        return;
      }
      hidePredictiveResults();
    });
  }

  /* ------------------------------------------------------ predictive search */

  var predictiveController = null;

  function hidePredictiveResults() {
    $$('[data-predictive-results]').forEach(function (panel) {
      panel.innerHTML = '';
      panel.setAttribute('hidden', 'hidden');
    });
  }

  function renderPredictiveJSON(panel, products, emptyText) {
    panel.innerHTML = '';

    if (!products || products.length === 0) {
      if (!emptyText) {
        panel.setAttribute('hidden', 'hidden');
        return;
      }
      var empty = document.createElement('div');
      empty.className = 'nav-search-results__empty';
      empty.textContent = emptyText;
      panel.appendChild(empty);
      panel.removeAttribute('hidden');
      return;
    }

    products.slice(0, 8).forEach(function (product) {
      var button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('data-predictive-url', product.url || '');
      button.appendChild(document.createTextNode(product.title || ''));

      var meta = product.product_type || product.vendor || '';
      if (meta) {
        var span = document.createElement('span');
        span.textContent = meta;
        button.appendChild(span);
      }
      panel.appendChild(button);
    });
    panel.removeAttribute('hidden');
  }

  var predictiveGlobalsBound = false;

  function initPredictiveSearch() {
    var forms = $$('[data-predictive-search]');
    if (forms.length === 0) return;

    forms.forEach(function (form) {
      if (!claim(form, 'psearch')) return;
      var input = $('[data-predictive-input]', form);
      var panel = $('[data-predictive-results]', form);
      if (!input || !panel) return;

      // The panel may carry its localized empty-state string; if it does not,
      // we simply hide the panel rather than inventing English copy.
      var emptyText =
        panel.getAttribute('data-empty-text') || strings.searchNoResults || '';

      var debounceTimer = null;

      function query() {
        var term = input.value.trim();
        if (term.length < 2) {
          panel.innerHTML = '';
          panel.setAttribute('hidden', 'hidden');
          return;
        }
        if (!routes.predictive_search_url) return;

        if (predictiveController) {
          try {
            predictiveController.abort();
          } catch (e) {
            /* nothing to abort */
          }
        }
        predictiveController =
          typeof AbortController !== 'undefined' ? new AbortController() : null;
        var signal = predictiveController ? predictiveController.signal : undefined;

        var base =
          routes.predictive_search_url +
          '?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product';

        // 1st choice: the Section Rendering API version, per SPEC.
        fetch(base + '&section_id=predictive-search', {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          signal: signal
        })
          .then(function (res) {
            if (!res.ok) throw new Error('no predictive section');
            return res.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = $('[data-predictive-results]', doc) || doc.body;
            var markup = fresh ? fresh.innerHTML.trim() : '';
            if (!markup) throw new Error('empty predictive section');
            panel.innerHTML = markup;
            panel.removeAttribute('hidden');
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            // 2nd choice: the always-available JSON endpoint.
            return fetch(base.replace('/suggest', '/suggest.json'), {
              headers: { Accept: 'application/json' },
              credentials: 'same-origin',
              signal: signal
            })
              .then(function (res) {
                if (!res.ok) throw new Error('no predictive json');
                return res.json();
              })
              .then(function (json) {
                var products =
                  (json && json.resources && json.resources.results &&
                    json.resources.results.products) ||
                  [];
                renderPredictiveJSON(panel, products, emptyText);
              })
              .catch(function (jsonErr) {
                if (jsonErr && jsonErr.name === 'AbortError') return;
                // Degrade completely: hide the panel, the form still submits.
                panel.innerHTML = '';
                panel.setAttribute('hidden', 'hidden');
              });
          });
      }

      on(input, 'input', function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(query, 250);
      });

      on(input, 'focus', function () {
        if (input.value.trim().length >= 2 && panel.innerHTML.trim()) {
          panel.removeAttribute('hidden');
        }
      });

      // Results built from JSON navigate on click.
      on(panel, 'click', function (event) {
        var button = event.target.closest && event.target.closest('[data-predictive-url]');
        if (!button) return;
        var url = button.getAttribute('data-predictive-url');
        if (url) {
          event.preventDefault();
          window.location.href = url;
        }
      });

      // Submitting always falls through to the real /search page.
      on(form, 'submit', function () {
        clearTimeout(debounceTimer);
        panel.setAttribute('hidden', 'hidden');
      });
    });

    if (predictiveGlobalsBound) return;
    predictiveGlobalsBound = true;

    // Clicking away closes the panel.
    on(document, 'click', function (event) {
      if (event.target.closest && event.target.closest('[data-predictive-search]')) return;
      hidePredictiveResults();
    });
  }

  /* ------------------------------------------------------------- slideshow */

  function initSlideshows() {
    $$('[data-slideshow]').forEach(function (root) {
      if (!claim(root, 'slideshow')) return;
      var slides = $$('[data-slide]', root);
      var dots = $$('[data-slide-to]', root);
      if (slides.length < 2) return;

      var index = 0;
      var timer = null;
      var speed = parseInt(root.getAttribute('data-autoplay-speed'), 10);
      if (!speed || isNaN(speed)) speed = 6000;
      var autoplay = root.getAttribute('data-autoplay') !== 'false';

      function show(next) {
        index = ((next % slides.length) + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.classList.toggle('active', i === index);
        });
        dots.forEach(function (dot, i) {
          dot.classList.toggle('active', i === index);
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      function start() {
        // Reduced motion: no auto-advance at all — the dots still work.
        if (!autoplay || prefersReducedMotion || timer) return;
        timer = setInterval(function () {
          show(index + 1);
        }, speed);
      }

      function stop() {
        clearInterval(timer);
        timer = null;
      }

      dots.forEach(function (dot) {
        on(dot, 'click', function (event) {
          event.preventDefault();
          var target = parseInt(dot.getAttribute('data-slide-to'), 10);
          if (!isNaN(target)) show(target);
        });
      });

      on(root, 'mouseenter', stop);
      on(root, 'mouseleave', start);
      on(root, 'focusin', stop);
      on(root, 'focusout', start);
      on(document, 'visibilitychange', function () {
        if (document.hidden) stop();
        else start();
      });

      show(0);
      start();
    });
  }

  /* -------------------------------------------------------- recently viewed */
  /* localStorage key "jk-recently-viewed" — the exact key from JSX line 2385.
     Every read and write is wrapped, exactly as the SPA guarded it, so a
     browser with storage disabled degrades to "no recently viewed" rather
     than throwing. */

  var RECENT_KEY = 'jk-recently-viewed';
  var RECENT_MAX = 8;
  var recentCache = null; // in-memory mirror; hover fires often, parsing must not

  function readRecent() {
    if (recentCache) return recentCache;
    try {
      var stored = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      recentCache = Array.isArray(stored) ? stored : [];
    } catch (e) {
      recentCache = [];
    }
    return recentCache;
  }

  function pushRecent(handle) {
    if (!handle) return readRecent();
    var list = readRecent();
    if (list[0] === handle) return list;
    var next = [handle].concat(
      list.filter(function (h) {
        return h !== handle;
      })
    );
    next = next.slice(0, RECENT_MAX);
    recentCache = next;
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (e) {
      // storage unavailable — recently viewed still works for this session
    }
    return next;
  }

  function currentProductHandle() {
    var marked = $('[data-product-page][data-product-handle]');
    if (marked) return marked.getAttribute('data-product-handle');
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function cloneBottleSvg() {
    var source =
      $('[data-recently-viewed] [data-bottle] svg') ||
      $('.product-card__media-spin svg') ||
      $('[data-bottle-svg] svg');
    return source ? source.cloneNode(true) : null;
  }

  function buildRecentCard(product) {
    var card = document.createElement('div');
    card.className = 'product-card';
    card.setAttribute('data-product-card', '');
    card.setAttribute('data-product-handle', product.handle || '');
    card.setAttribute('data-product-title', product.title || '');

    var media = document.createElement('a');
    media.href = product.url || '#';
    media.setAttribute('data-product-card-media', '');
    media.setAttribute('aria-label', product.title || '');

    if (product.featured_image) {
      // Real photo: fills the card width, intrinsic ratio sets the height. Mirrors the
      // product-card snippet's --real branch exactly (no floating-bottle stage).
      media.className = 'product-card__media product-card__media--real';
      var img = document.createElement('img');
      img.className = 'product-card__img';
      img.src = product.featured_image;
      img.alt = product.title || '';
      img.loading = 'lazy';
      var preview = product.media && product.media[0] && product.media[0].preview_image;
      if (preview && preview.width && preview.height) {
        img.width = preview.width;
        img.height = preview.height;
      }
      media.appendChild(img);
    } else {
      // Same fallback rule as every Liquid render: no photo -> the floating SVG bottle.
      media.className = 'product-card__media product-card__media--placeholder';
      var pop = document.createElement('div');
      pop.className = 'product-card__media-pop';
      var spin = document.createElement('div');
      spin.className = 'product-card__media-spin';
      var bottle = cloneBottleSvg();
      if (bottle) spin.appendChild(bottle);
      pop.appendChild(spin);
      media.appendChild(pop);
    }
    card.appendChild(media);

    var info = document.createElement('div');
    info.className = 'product-card__info';

    var title = document.createElement('h3');
    title.className = 'product-card__title';
    var titleLink = document.createElement('a');
    titleLink.href = product.url || '#';
    titleLink.textContent = product.title || '';
    title.appendChild(titleLink);
    info.appendChild(title);

    if (typeof product.price !== 'undefined' && product.price !== null) {
      var price = document.createElement('div');
      price.className = 'product-card__price';
      price.textContent = formatMoney(product.price);
      info.appendChild(price);
    }

    card.appendChild(info);
    return card;
  }

  function renderRecentlyViewed() {
    var container = $('[data-recently-viewed]');
    if (!container) return;

    var here = currentProductHandle();
    var handles = readRecent().filter(function (handle) {
      return handle && handle !== here;
    });
    if (handles.length === 0) return;

    var base = shopifyRoot.replace(/\/$/, '');
    Promise.all(
      handles.map(function (handle) {
        return fetch(base + '/products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .catch(function () {
            return null;
          });
      })
    )
      .then(function (products) {
        var fragment = document.createDocumentFragment();
        var rendered = 0;
        products.forEach(function (product) {
          if (!product || !product.handle) return;
          fragment.appendChild(buildRecentCard(product));
          rendered += 1;
        });
        if (rendered === 0) return;
        container.innerHTML = '';
        container.appendChild(fragment);
        var wrapper = container.closest ? container.closest('.recently-viewed') : null;
        if (wrapper) wrapper.removeAttribute('hidden');
        container.removeAttribute('hidden');
      })
      .catch(function () {
        /* leave the section empty rather than half-built */
      });
  }

  function initRecentlyViewed() {
    // Product pages record themselves.
    var here = currentProductHandle();
    if (here) pushRecent(here);

    // The SPA also recorded a "view" on card hover / click (JSX ProductCard
    // onMouseEnter + onClick -> onView). Same behaviour, delegated.
    function record(event) {
      var card = event.target.closest && event.target.closest('[data-product-card]');
      if (!card) return;
      pushRecent(card.getAttribute('data-product-handle'));
    }
    on(document, 'mouseover', record, { passive: true });
    on(document, 'click', record, { passive: true });

    renderRecentlyViewed();
  }

  /* ----------------------------------------------------------------- FAB */

  function initFab() {
    var stack = $('[data-fab-stack]') || $('.fab-stack');
    if (!stack || !claim(stack, 'fab')) return;
    var toggle = $('[data-fab-toggle]', stack) || $('.fab-toggle', stack);
    if (!toggle) return;

    toggle.setAttribute('aria-expanded', 'false');
    on(toggle, 'click', function (event) {
      event.preventDefault();
      var willOpen = !stack.classList.contains('open');
      stack.classList.toggle('open', willOpen);
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });

    on(document, 'click', function (event) {
      if (!stack.isConnected) return;
      if (event.target.closest && event.target.closest('[data-fab-stack], .fab-stack')) return;
      stack.classList.remove('open');
      toggle.setAttribute('aria-expanded', 'false');
    });

    on(document, 'keydown', function (event) {
      if (event.key !== 'Escape' && event.keyCode !== 27) return;
      if (!stack.classList.contains('open')) return;
      stack.classList.remove('open');
      toggle.setAttribute('aria-expanded', 'false');
      if (toggle.focus) toggle.focus();
    });
  }

  /* ------------------------------------------------- patron pop (social proof) */
  /*
   * MARKUP CONTRACT (rendered by sections/footer.liquid — see notes):
   *   <div class="patron-pop" data-patron-pop role="status"
   *        data-cities='["<localized city>", ...]'
   *        data-products='["<title>", ...]'   (optional)
   *   >
   *     <div class="patron-pop__mark">JK</div>
   *     <div class="patron-pop__body">
   *       <p class="patron-pop__eyebrow">…</p>
   *       <p class="patron-pop__title"><strong data-pop-product></strong> — <span data-pop-city></span></p>
   *       <p class="patron-pop__meta" data-pop-meta data-template="…[[minutes]]…"></p>
   *     </div>
   *     <button type="button" class="patron-pop__close" data-pop-dismiss>✕</button>
   *   </div>
   * Every string inside it is server-rendered through | t. This file supplies
   * only numbers and the show/hide choreography.
   */

  var POP_DISMISS_KEY = 'jk-pop-dismissed';

  function popIsDismissed() {
    try {
      return window.sessionStorage.getItem(POP_DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function rememberPopDismissal() {
    try {
      window.sessionStorage.setItem(POP_DISMISS_KEY, '1');
    } catch (e) {
      // session storage unavailable — dismissal holds for this page view only
    }
  }

  function initPatronPop() {
    var pop = $('[data-patron-pop]');
    if (!pop || !claim(pop, 'pop')) return;

    var cities = parseJSONAttr(pop, 'data-cities') || [];
    var products = parseJSONAttr(pop, 'data-products');
    if (!products || products.length === 0) {
      // Drawn straight from the live catalog on the page, exactly as the SPA
      // built POP_PRODUCTS from the live arrays (JSX 1231-1234).
      products = $$('[data-product-card][data-product-title]')
        .map(function (card) {
          return card.getAttribute('data-product-title');
        })
        .filter(function (title, i, all) {
          return title && all.indexOf(title) === i;
        });
    }
    if (cities.length === 0 || products.length === 0) return;

    var productEl = $('[data-pop-product]', pop);
    var cityEl = $('[data-pop-city]', pop);
    var metaEl = $('[data-pop-meta]', pop);
    var metaTemplate = metaEl ? metaEl.getAttribute('data-template') || '' : '';
    var dismissBtn = $('[data-pop-dismiss]', pop);

    var dismissed = popIsDismissed();
    var showTimer = null;
    var hideTimer = null;
    var cycleIndex = 0;

    function hide() {
      pop.classList.remove('show');
    }

    on(dismissBtn, 'click', function (event) {
      event.preventDefault();
      dismissed = true;
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      hide();
      rememberPopDismissal();
    });

    if (dismissed || prefersReducedMotion) return;

    function runCycle(delay) {
      showTimer = setTimeout(function () {
        if (dismissed) return;
        var city = cities[Math.floor(Math.random() * cities.length)];
        var product = products[cycleIndex % products.length];
        var minutesAgo = 2 + Math.floor(Math.random() * 26);
        cycleIndex += 1;

        if (productEl) productEl.textContent = product;
        if (cityEl) cityEl.textContent = city;
        if (metaEl) {
          metaEl.textContent = metaTemplate
            ? metaTemplate.replace('[[minutes]]', minutesAgo)
            : '';
        }
        pop.classList.add('show');

        hideTimer = setTimeout(function () {
          hide();
          runCycle(9000 + Math.random() * 7000);
        }, 5600);
      }, delay);
    }

    // 4500ms before the first notice — the SPA's opening beat (JSX 2585).
    runCycle(4500);
  }

  /* ------------------------------------------------------------------ boot */

  function init() {
    initToast();
    initCart();
    initHeader();
    initPredictiveSearch();
    initSlideshows();
    initRecentlyViewed();
    initFab();
    initPatronPop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run everything the theme editor can re-render underneath us. Every
  // initializer below is claim()-guarded, so re-running never double-binds.
  document.addEventListener('shopify:section:load', function () {
    initHeader();
    initPredictiveSearch();
    initSlideshows();
    initFab();
    initPatronPop();
    renderRecentlyViewed();
  });

  /* Small surface for the sibling bundles (bundle-builder.js, compare.js,
     scent-finder.js) so they never re-implement the cart or the toast. */
  window.JK = window.JK || {};
  window.JK.openCart = openCart;
  window.JK.closeCart = closeCart;
  window.JK.refreshCart = refreshCart;
  window.JK.showToast = showToast;
  window.JK.showToastMessage = showToastMessage;
  window.JK.playCapClick = playCapClick;
  window.JK.formatMoney = formatMoney;
  window.JK.pushRecentlyViewed = pushRecent;
})();
